/**
 * Team Peer Evaluations Page
 *
 * Full page view of all peer evaluations for the manager's team.
 * Styled identically to TeamEvaluationsPage with statistics, filters, and table.
 * Accessible via /team/peer-feedback
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { Layout } from '../../components/Layout';
import { useTeamMembers } from './useTeam';
import { peerEvaluationKeys, type PeerEvaluation, PeerEvaluationCreateModal, useDeletePeerEvaluation } from '../peer-evaluations';
import { peerEvaluationApi } from '../../lib/peerEvaluationApi';
import { useAuth } from '../auth';
import { useToast } from '../../components/Toast';

type PeerEvaluationStatus = 'concept' | 'ingediend';

const STATUS_CONFIG: Record<PeerEvaluationStatus, { label: string; color: string; bgColor: string }> = {
  concept: { label: 'Concept', color: 'text-yellow-800', bgColor: 'bg-yellow-100' },
  ingediend: { label: 'Ingediend', color: 'text-green-800', bgColor: 'bg-green-100' },
};

export function TeamPeerEvaluationsPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<PeerEvaluationStatus | 'all'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'status' | 'employee' | 'evaluator'>('date');

  // Modal state
  const [showEmployeeSelect, setShowEmployeeSelect] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<{ id: number; name: string } | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');

  // Copy link state - tracks which peer evaluation's link was just copied
  const [copiedLinkId, setCopiedLinkId] = useState<number | null>(null);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<PeerEvaluation | null>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  // Hooks
  const { hasRole } = useAuth();
  const { showToast } = useToast();
  const deleteMutation = useDeletePeerEvaluation();
  const canDelete = hasRole(['hr', 'admin']);

  const { data: teamMembers, isLoading: teamLoading } = useTeamMembers();

  // Focus trap voor delete modal (accessibility)
  useEffect(() => {
    if (deleteConfirm && cancelButtonRef.current) {
      cancelButtonRef.current.focus();
    }
  }, [deleteConfirm]);

  // Get all team member IDs
  const teamMemberIds = useMemo(() => {
    return teamMembers?.map((m) => m.employeeId) || [];
  }, [teamMembers]);

  // Filter team members for employee selector
  const filteredTeamMembers = useMemo(() => {
    if (!teamMembers) return [];
    if (!employeeSearchQuery.trim()) return teamMembers;
    const query = employeeSearchQuery.toLowerCase();
    return teamMembers.filter(
      (member) =>
        member.name.toLowerCase().includes(query) ||
        member.email.toLowerCase().includes(query)
    );
  }, [teamMembers, employeeSearchQuery]);

  // Use useQueries to fetch peer evaluations for all team members in parallel
  const peerEvalQueries = useQueries({
    queries: teamMemberIds.map((employeeId) => ({
      queryKey: peerEvaluationKeys.list(employeeId),
      queryFn: () => peerEvaluationApi.getByEmployeeId(employeeId),
      enabled: employeeId > 0,
    })),
  });

  const isLoading = teamLoading || peerEvalQueries.some((q) => q.isLoading);
  const hasError = peerEvalQueries.some((q) => q.isError);

  // Combine all peer evaluations from all team members
  const allPeerEvaluations = useMemo(() => {
    const evals: PeerEvaluation[] = [];
    peerEvalQueries.forEach((q) => {
      if (q.data?.data) {
        evals.push(...q.data.data);
      }
    });
    // Remove duplicates by ID
    const uniqueMap = new Map<number, PeerEvaluation>();
    evals.forEach((e) => uniqueMap.set(e.id, e));
    return Array.from(uniqueMap.values());
  }, [peerEvalQueries]);

  // Filter by status
  const filteredPeerEvaluations = statusFilter === 'all'
    ? allPeerEvaluations
    : allPeerEvaluations.filter((e) => e.status === statusFilter);

  // Apply sorting
  const sortedPeerEvaluations = [...filteredPeerEvaluations].sort((a, b) => {
    if (sortBy === 'date') {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }
    if (sortBy === 'status') {
      const statusOrder = ['concept', 'ingediend'];
      return statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status);
    }
    if (sortBy === 'employee') {
      return a.employeeName.localeCompare(b.employeeName);
    }
    // sortBy === 'evaluator'
    return a.evaluatorName.localeCompare(b.evaluatorName);
  });

  // Calculate statistics
  const stats = {
    total: allPeerEvaluations.length,
    concept: allPeerEvaluations.filter((e) => e.status === 'concept').length,
    ingediend: allPeerEvaluations.filter((e) => e.status === 'ingediend').length,
  };

  // Handlers
  const handleCreateNew = () => {
    setEmployeeSearchQuery('');
    setShowEmployeeSelect(true);
  };

  const handleEmployeeSelect = (employeeId: number, employeeName: string) => {
    setSelectedEmployee({ id: employeeId, name: employeeName });
    setShowEmployeeSelect(false);
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setSelectedEmployee(null);
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    setSelectedEmployee(null);
  };

  // Copy public link to clipboard
  const handleCopyLink = async (peerEvalId: number, token: string) => {
    const publicUrl = `${window.location.origin}/evaluatie/${token}`;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopiedLinkId(peerEvalId);
      setTimeout(() => setCopiedLinkId(null), 2000);
    } catch {
      alert('Kon link niet kopiëren naar klembord');
    }
  };

  // Delete peer evaluation
  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await deleteMutation.mutateAsync({
        id: deleteConfirm.id,
        employeeId: deleteConfirm.employeeId,
      });
      setDeleteConfirm(null);
      showToast('Peer evaluatie verwijderd', 'success');
    } catch {
      showToast('Fout bij verwijderen', 'error');
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Peer evaluaties laden...</span>
        </div>
      </Layout>
    );
  }

  if (hasError) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-600">Er is een fout opgetreden bij het laden van peer evaluaties.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header with breadcrumb */}
        <div className="mb-6">
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <Link to="/team" className="hover:text-blue-600">Mijn Team</Link>
            <span>›</span>
            <span className="text-gray-900">Peer Evaluaties</span>
          </nav>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Team Peer Evaluaties</h1>
              <p className="mt-1 text-sm text-gray-500">
                Overzicht van alle 360° feedback peer evaluaties voor je teamleden.
              </p>
            </div>
            <button
              onClick={handleCreateNew}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nieuw
            </button>
          </div>
        </div>

        {/* Statistics */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200 text-center">
            <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-sm text-gray-500">Totaal</p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 text-center">
            <p className="text-3xl font-bold text-yellow-700">{stats.concept}</p>
            <p className="text-sm text-yellow-600">Concept</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
            <p className="text-3xl font-bold text-green-700">{stats.ingediend}</p>
            <p className="text-sm text-green-600">Ingediend</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="p-4 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-500">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as PeerEvaluationStatus | 'all')}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Alle</option>
                <option value="concept">Concept</option>
                <option value="ingediend">Ingediend</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-500">Sorteer:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'date' | 'status' | 'employee' | 'evaluator')}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="date">Datum</option>
                <option value="status">Status</option>
                <option value="employee">Medewerker</option>
                <option value="evaluator">Evaluator</option>
              </select>
            </div>

            <div className="ml-auto text-sm text-gray-500">
              {sortedPeerEvaluations.length} resultaten
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Medewerker
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Evaluator
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Datum
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Acties
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedPeerEvaluations.map((peerEval) => (
                  <tr key={peerEval.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-blue-600 text-xs font-medium">
                            {peerEval.employeeName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <Link
                            to={`/medewerkers/${peerEval.employeeId}`}
                            className="text-sm font-medium text-gray-900 hover:text-blue-600"
                          >
                            {peerEval.employeeName}
                          </Link>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                          <span className="text-purple-600 text-xs font-medium">
                            {peerEval.evaluatorName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <Link
                            to={`/medewerkers/${peerEval.evaluatorId}`}
                            className="text-sm font-medium text-gray-900 hover:text-blue-600"
                          >
                            {peerEval.evaluatorName}
                          </Link>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(peerEval.createdAt).toLocaleDateString('nl-NL', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_CONFIG[peerEval.status].bgColor} ${STATUS_CONFIG[peerEval.status].color}`}>
                        {STATUS_CONFIG[peerEval.status].label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        {/* Copy link button */}
                        <button
                          onClick={() => handleCopyLink(peerEval.id, peerEval.token)}
                          className={`p-2 rounded-lg transition-colors ${
                            copiedLinkId === peerEval.id
                              ? 'text-green-600 bg-green-50'
                              : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                          }`}
                          title={copiedLinkId === peerEval.id ? 'Gekopieerd!' : 'Kopieer link'}
                        >
                          {copiedLinkId === peerEval.id ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                          )}
                        </button>
                        {/* Delete button - HR/Admin only */}
                        {canDelete && (
                          <button
                            onClick={() => setDeleteConfirm(peerEval)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Verwijderen"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                        {/* Print button */}
                        <button
                          onClick={() => window.open(`/peer-evaluatie/${peerEval.id}/print`, '_blank')}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Afdrukken"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => navigate(`/peer-evaluatie/${peerEval.id}`)}
                          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Bekijken
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {sortedPeerEvaluations.length === 0 && (
              <div className="p-12 text-center">
                <svg
                  className="w-12 h-12 text-gray-300 mx-auto mb-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <p className="text-gray-500">
                  Geen peer evaluaties gevonden
                  {statusFilter !== 'all' && ' met deze filters'}.
                </p>
                <button
                  onClick={handleCreateNew}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Eerste peer evaluatie aanmaken
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Employee Selection Modal */}
        {showEmployeeSelect && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Selecteer Medewerker</h3>
              <p className="text-sm text-gray-600 mb-4">
                Kies de medewerker die beoordeeld moet worden.
              </p>

              {/* Search filter */}
              <div className="relative mb-4">
                <input
                  type="text"
                  placeholder="Zoek op naam of email..."
                  value={employeeSearchQuery}
                  onChange={(e) => setEmployeeSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {filteredTeamMembers.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    Geen medewerkers gevonden voor "{employeeSearchQuery}"
                  </p>
                ) : (
                  filteredTeamMembers.map((member) => (
                    <button
                      key={member.id}
                      onClick={() => handleEmployeeSelect(member.employeeId, member.name)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-300 transition-colors text-left"
                    >
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 font-medium">
                          {member.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{member.name}</p>
                        <p className="text-sm text-gray-500 truncate">{member.email}</p>
                      </div>
                    </button>
                  ))
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowEmployeeSelect(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuleren
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Peer Evaluation Create Modal */}
        {showCreateModal && selectedEmployee && (
          <PeerEvaluationCreateModal
            isOpen={showCreateModal}
            onClose={handleCloseCreateModal}
            employeeId={selectedEmployee.id}
            employeeName={selectedEmployee.name}
            onSuccess={handleCreateSuccess}
          />
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-modal-title"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setDeleteConfirm(null);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setDeleteConfirm(null);
              }
            }}
          >
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <h3 id="delete-modal-title" className="text-lg font-bold text-gray-900">
                  Peer evaluatie verwijderen
                </h3>
              </div>

              <p className="text-gray-600 mb-2">
                Weet je zeker dat je deze peer evaluatie wilt verwijderen?
              </p>
              <p className="text-sm text-gray-500 mb-1">
                Medewerker: <strong>{deleteConfirm.employeeName}</strong>
              </p>
              <p className="text-sm text-gray-500 mb-2">
                Evaluator: <strong>{deleteConfirm.evaluatorName}</strong>
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Status:{' '}
                <span className={`font-medium ${deleteConfirm.status === 'ingediend' ? 'text-green-700' : 'text-yellow-700'}`}>
                  {STATUS_CONFIG[deleteConfirm.status].label}
                </span>
              </p>
              {deleteConfirm.status === 'ingediend' && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-orange-800 font-medium">
                    ⚠️ Let op: Dit is een ingediende evaluatie
                  </p>
                  <p className="text-xs text-orange-700 mt-1">
                    De feedback en scores van {deleteConfirm.evaluatorName} worden permanent verwijderd.
                  </p>
                </div>
              )}
              <p className="text-sm text-red-600 mb-6">
                Deze actie kan niet ongedaan worden gemaakt.
              </p>

              <div className="flex gap-3 justify-end">
                <button
                  ref={cancelButtonRef}
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'Verwijderen...' : 'Ja, verwijderen'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
